import type { LogItemType } from "@/types/common/logs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Avatar,
  AvatarFallback,
  Badge,
  Icon,
} from "@elaniintc/goes-design-system";
import dayjs from "dayjs";
import { getDataTypeIcon } from "./form/form-correction-table";
import { Renderify } from "renderify-js";

const mapFieldTypeToVariableType = (fieldType: string): string => {
  switch (fieldType) {
    case "text":
    case "textarea":
    case "email":
    case "phone":
    case "select":
    case "radio":
      return "str";
    case "number":
    case "currency":
      return "num";
    case "date":
    case "datetime":
      return "date";
    case "checkbox":
      return "bool";
    case "file":
    case "image":
      return "file";
    case "list":
      return "array";
    default:
      return "str";
  }
};

const PreventionLog = ({ log }: { log: LogItemType }) => {
  const formatDate = (date: Date | string) => {
    const d = dayjs(date);

    const day = d.format("D");
    const month = d.format("MMM");
    const rest = d.format(", YYYY | HH:mm");

    return `${day} ${month.charAt(0).toUpperCase() + month.slice(1)}${rest}`;
  };

  // Flatten corrections across all revisions; each correction represents one analyst review round
  const corrections =
    log.revisions?.flatMap((revision) => revision.corrections ?? []) ?? [];

  return (
    <div className="w-full flex flex-col gap-3">
      <div className="flex gap-2">
        <Avatar className="shrink-0" shape="square" size="2xs">
          <AvatarFallback color="action" shape="square">
            <Icon type="fa" size="xs" name="file-pen" />
          </AvatarFallback>
        </Avatar>

        <span className="text-body font-bold">Revisión</span>
      </div>

      {corrections.map((correction) => {
        const author = correction.comments?.[0]?.creator;
        const generalComment = correction.comments?.[0]?.text;

        return (
          <Accordion
            key={correction.id}
            type="single"
            collapsible
            className="text-body rounded-lg border border-neutral-quiet **:hover:no-underline"
          >
            <AccordionItem value="item-1">
              <AccordionTrigger className="px-3 py-2 cursor-pointer">
                <div className="w-full flex justify-between items-center gap-1">
                  <div className="flex items-center gap-1">
                    <span className="text-body">Por</span>

                    <Avatar className="shrink-0" size="xs">
                      <AvatarFallback color="action">
                        {author?.name?.substring(0, 1)}
                      </AvatarFallback>
                    </Avatar>

                    <span className="text-action">{`${author?.name ?? ""} ${author?.last_name ?? ""}`}</span>
                  </div>

                  <span className="text-body">
                    {formatDate(correction.created_at)}
                  </span>
                </div>
              </AccordionTrigger>

              <AccordionContent className="border-t border-neutral-quiet ">
                <div className="flex flex-col gap-2 py-4 px-3">
                  <span className="text-body-loud font-bold">
                    Evaluación general
                  </span>

                  <div className="rounded-lg bg-neutral-object-accent-silent border border-neutral-quiet p-2">
                    <div className="flex items-center gap-2">
                      <Icon
                        type="fa"
                        name="comment"
                        className="text-warning-quiet"
                      />
                      <span className="text-warning">Comentario general: </span>
                    </div>

                    <Renderify
                      condition={
                        !!generalComment &&
                        generalComment !==
                          "No se agregaron comentarios adicionales."
                      }
                      elseShow={
                        <span className="text-body-quiet italic">
                          {generalComment ||
                            "No se agregaron comentarios adicionales."}
                        </span>
                      }
                    >
                      <span className="text-body">{generalComment}</span>
                    </Renderify>
                  </div>
                </div>

                {correction.field_corrections?.map((fc) => {
                  const isDenied = fc.status === "DENIED";
                  const analystComment = fc.comments?.[0]?.text;
                  const hasChanges =
                    fc.previous_value != null && fc.new_value != null;

                  return (
                    <div
                      key={fc.id}
                      className="flex flex-col px-3 py-4 gap-3 border-t border-neutral-quiet"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-body-quiet">
                          {getDataTypeIcon(fc.field?.type ?? "")}
                        </span>

                        <span className="text-body font-bold">
                          {fc.field?.label}
                        </span>

                        <span className="text-body-quiet">
                          {mapFieldTypeToVariableType(fc.field?.type ?? "")}
                        </span>
                      </div>

                      <Renderify condition={hasChanges}>
                        <div className="rounded-lg bg-neutral-object-accent-silent border border-neutral-quiet p-2">
                          <span className="line-through text-body-quiet">
                            {fc.previous_value}
                          </span>{" "}
                          <Icon type="fa" name="arrow-right" />{" "}
                          <span className="text-body-loud">{fc.new_value}</span>
                        </div>
                      </Renderify>

                      <div className="rounded-lg bg-neutral-object-accent-silent border border-neutral-quiet p-2">
                        <div className="flex items-center gap-1">
                          <span className="text-body">Marcado como</span>

                          {isDenied ? (
                            <Badge size="xs" variant="danger">
                              Denegado
                              <Icon
                                type="fas"
                                name="x"
                                size="xs"
                                className="text-danger-quiet"
                              />
                            </Badge>
                          ) : (
                            <Badge size="xs" variant="success">
                              Aprobado
                              <Icon
                                type="fas"
                                name="check"
                                size="xs"
                                className="text-success-quiet"
                              />
                            </Badge>
                          )}
                        </div>
                      </div>

                      {isDenied && (
                        <div className="rounded-lg bg-neutral-object-accent-silent border border-neutral-quiet p-2">
                          <div className="flex items-center gap-2">
                            <Icon
                              type="fa"
                              name="comment"
                              className="text-warning-quiet"
                            />
                            <span className="text-warning">Comentario: </span>
                          </div>

                          <span className="text-body">
                            {analystComment || "No hay comentario."}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        );
      })}
    </div>
  );
};

export default PreventionLog;
